import React from 'react';
import clsx from 'clsx';
import styles from '@danske/sapphire-css/components/notificationBadge/notificationBadge.module.css';
import {
  SapphireStyleProps,
  useSapphireStyleProps,
  useThemeCheck,
} from '../../utils';
import { filterDOMProps } from 'react-aria/filterDOMProps';
import { GlobalDomAttributes } from '../../utils/types';

export type NotificationBadgeProps = {
  /**
   * Boolean to perform or not perform the animation
   * @default 'true'
   */
  animate?: boolean;
  /**
   * Badge will be added relative to the children. It is rendered inline if there is no children provided
   * */
  children?: React.ReactNode;
  /**
   * The shape of the container, based on which badge should be positioned. Badge is positioned on the border of the container
   * @default 'circle'
   * */
  containerType?: 'circle' | 'rectangle';
  /**
   * Number to show in badge
   * */
  count: number;
  /**
   * The variation of badge which determines the badge's color.
   *
   * @default 'primary'
   */
  variant?: 'primary' | 'secondary';
  /**
   * @default 'md'
   */
  size?: 'lg' | 'md' | 'sm';
  /**
   * Boolean to show/hide the badge
   *
   * @default true
   */
  show?: boolean;
} & SapphireStyleProps &
  GlobalDomAttributes &
  ({ 'aria-labelledby': string } | { 'aria-label': string });

export const NotificationBadge = (
  props: NotificationBadgeProps
): JSX.Element => {
  useThemeCheck();

  const {
    animate = true,
    children,
    count,
    containerType = 'circle',
    variant = 'primary',
    size = 'md',
    show = true,
    ...otherProps
  } = props;
  const { styleProps } = useSapphireStyleProps(props);

  const badge = () =>
    show || animate ? (
      <span
        {...filterDOMProps(otherProps, { global: true, labelable: true })}
        role="status"
        className={clsx(
          styleProps.className,
          styles['sapphire-notification-badge'],
          styles[`sapphire-notification-badge--${size}` as const],
          styles[`sapphire-notification-badge--${variant}` as const],
          {
            [styles[`sapphire-notification-badge--pop-in`]]: show && animate,
            [styles[`sapphire-notification-badge--pop-out`]]: !show && animate,
          }
        )}
        style={styleProps.style}
      >
        {size !== 'sm' && displayCount}
      </span>
    ) : (
      <></>
    );

  const displayCount = count.toString().length > 2 ? `99+` : count;
  return children ? (
    <span
      className={clsx(styles['sapphire-notification-badge-container'], {
        [styles['sapphire-notification-badge-container--rectangle']]:
          containerType === 'rectangle',
      })}
    >
      {children}
      {badge()}
    </span>
  ) : (
    badge()
  );
};
